import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

export const useEmailVerification = () => {
  const { user } = useAuthStore();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Query user data
  const {
    data: userData,
    isLoading,
    error,
    refetch,
  } = useQuery<{ data: { user: any } }>({
    queryKey: ['user'],
    queryFn: async () => {
      const response = await axios.get('/api/auth/me');
      return response.data;
    },
    enabled: !!user, // Only run if user is logged in
    refetchInterval: false, // We'll handle polling manually in the modal
  });

  // Check if email verification is required - simply check if email is not verified
  const isEmailVerified = userData?.data?.user?.emailVerified ?? false;
  const needsEmailVerification = !isEmailVerified;

  // Open verification modal
  const openVerificationModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  // Close verification modal
  const closeVerificationModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Handle successful verification
  const handleVerificationSuccess = useCallback(() => {
    setIsModalOpen(false);
    refetch(); // Refresh verification status
  }, [refetch]);

  // Check if user should be prompted for verification
  const shouldPromptForVerification = useCallback(() => {
    if (!user) return false;
    if (isLoading) return false;
    if (error) return false;
    
    return needsEmailVerification && !isEmailVerified;
  }, [user, isLoading, error, needsEmailVerification, isEmailVerified]);

  return {
    // State
    isModalOpen,
    isLoading,
    error,
    
    // Status
    needsEmailVerification,
    isEmailVerified,
    verificationStatus: {
      emailVerified: isEmailVerified,
      needsEmailVerification,
    },
    
    // Actions
    openVerificationModal,
    closeVerificationModal,
    handleVerificationSuccess,
    shouldPromptForVerification,
    
    // User info
    userEmail: user?.email,
  };
}; 